import React, { useEffect, useMemo, useState, ChangeEvent, useCallback } from 'react';
import { Modal, Button, Radio, Input, message } from 'antd';
import { Typography } from '../../../common';
import './transfer.modal.scss';
import { IoCloseOutline } from 'react-icons/io5';
import { AiOutlineUser } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useGetCalleeFromDestination } from 'hooks/useGetCalleeFromDestination';
import { SearchIconNew, TransferIcon } from 'assets';
import TransferModalExtensionsList from './TransferModalExtensionsList';
import { Callee } from '../../../../redux/slices/liveCalls.slice';

interface TransferCallModalProps {
    open: boolean;
    onClose: () => void;
    callees: Callee[];
    callUUID: string;
    callerIDNumber: string;
    callerIDName: string;
    destination: string;
    mode?: 'dnd' | 'regular';
    onConfirmTransfer?: (calleeUUID: string) => void;
    onTransfer?: (ext: string, participantUUID?: string) => void;
    excludeExts?: string[];
}

const TransferCallModal: React.FC<TransferCallModalProps> = ({
    open,
    onClose,
    onConfirmTransfer,
    onTransfer,
    callees: initialCallees,
    callUUID,
    callerIDNumber,
    callerIDName,
    destination,
    mode = 'dnd',
    excludeExts,
}) => {
    const [selectedLeg, setSelectedLeg] = useState<string>('');
    const [ext, setExt] = useState<string>('');

    const liveCalls = useSelector((state: RootState) => state.liveCalls.calls);

    const currentCall = useMemo(() => {
        return liveCalls.find(call => call.callUUID === callUUID);
    }, [liveCalls, callUUID]);

    const callees = useMemo(() => {
        return currentCall?.callees || initialCallees || [];
    }, [currentCall?.callees, initialCallees]);

    useEffect(() => {
        if (open && !currentCall) {
            onClose();
        }
    }, [open, currentCall, onClose]);

    const calleeLabel = useGetCalleeFromDestination(destination);

    const callee = callees?.[0];

    const currentCallerIDNumber = currentCall?.callerIDNumber || callerIDNumber;
    const currentCallerIDName = currentCall?.callerIDName || callerIDName;

    const handleConfirm = () => {
        if (selectedLeg && mode === 'dnd') {
            const targetUUID = (callee?.uuid && selectedLeg === callee?.uuid) ? callee?.uuid : callUUID;

            onConfirmTransfer?.(targetUUID);
            setSelectedLeg('');
            onClose();
        }
    };

    const handleCancel = () => {
        setSelectedLeg('');
        setExt('');
        onClose();
    };

    const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setExt(e.target?.value);
    }, []);

    const handleSubmit = useCallback(() => {
        if (!onTransfer) return;
        if (!ext) return;

        if (/[a-zA-Z]/.test(ext)) {
            message.error("Please enter a valid extension");
            return;
        }

        if (callee?.uuid && selectedLeg === callee?.uuid) {
            onTransfer(ext, callee.uuid);
        } else {
            onTransfer(ext);
        }

        setExt('');
        setSelectedLeg('');
    }, [onTransfer, ext, selectedLeg, callee]);

    const handleExtensionSelect = useCallback((selectedExt: string) => {
        if (callee?.uuid && selectedLeg === callee?.uuid) {
            onTransfer?.(selectedExt, callee?.uuid);
        } else {
            onTransfer?.(selectedExt);
        }
    }, [onTransfer, selectedLeg, callee]);

    useEffect(() => {
        if (open) {
            setSelectedLeg(currentCallerIDNumber);
        }
    }, [open, currentCallerIDNumber]);

    const isDndMode = mode === 'dnd';
    const isRegularMode = mode === 'regular';

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            footer={null}
            closeIcon={<IoCloseOutline color='#fff' size={30} />}
            width={isRegularMode ? 550 : 500}
            rootClassName="transfer-modal"
            centered
            className="transfer-modal"
            destroyOnClose
            styles={{
                content: {
                    margin: isRegularMode ? '10px 0' : undefined
                }
            }}
            bodyStyle={{ padding: 0 }}
        >
            <div className="transfer-modal-container">
                <div className="modal-header">
                    <Typography variant="subText-2" className="modal-title">
                        Transfer Call
                    </Typography>
                </div>

                <div className="modal-content">
                    {isRegularMode && (
                        <div className="search-section">
                            <div className="search-input-wrapper">
                                <img src={SearchIconNew} className="search-icon" />
                                <Input
                                    placeholder="Enter phone number or extension"
                                    value={ext}
                                    className='search-input'
                                    onChange={handleInputChange}
                                    style={{ border: 'none', background: 'transparent', fontSize: '16px', fontWeight: 500, width: "100%" }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                                />
                                {ext && (
                                    <button
                                        onClick={handleSubmit}
                                        className="search-submit-button"
                                    >
                                        <img src={TransferIcon} className='search-input-submit-icon' />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {currentCallerIDNumber && (
                        <div className="participants-section">
                            <Typography variant={isRegularMode ? "text-2" : "subHeading-1"} className="question-text">
                                Select participant to transfer:
                            </Typography>

                            <div className="participants-list">
                                <Radio.Group
                                    value={selectedLeg}
                                    onChange={(e) => setSelectedLeg(e.target.value)}
                                    className="participant-radio-group"
                                    style={{ width: '100%' }}
                                >
                                    {callee && (
                                        <div key={callee.uuid} className="participant-item">
                                            <Radio value={callee.uuid} className="participant-radio">
                                                <div className="participant-content">
                                                    <div className="participant-info">
                                                        <div className="participant-avatar">
                                                            <AiOutlineUser color='#9399b5' size={20} />
                                                        </div>
                                                        <div>
                                                            <Typography variant="small-heading-1" className="participant-name">
                                                                {calleeLabel || 'Unknown'}
                                                            </Typography>
                                                            <Typography variant="small-text-4" className="participant-extension">
                                                                {callee?.number || 'Unknown'}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                    <Typography variant="small-text-3" className="role-text participant-role-text">
                                                        Callee
                                                    </Typography>
                                                </div>
                                            </Radio>
                                        </div>
                                    )}
                                    <div className="participant-item">
                                        <Radio value={currentCallerIDNumber} className="participant-radio">
                                            <div className="participant-content">
                                                <div className="participant-info">
                                                    <div className="participant-avatar">
                                                        <AiOutlineUser color='#9399b5' size={20} />
                                                    </div>
                                                    <div>
                                                        <Typography variant="small-heading-1" className="participant-name">
                                                            {currentCallerIDName || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="small-text-4" className="participant-extension">
                                                            {currentCallerIDNumber || 'Unknown Extension'}
                                                        </Typography>
                                                    </div>
                                                </div>
                                                <Typography variant="small-text-3" className="role-text participant-role-text">
                                                    Caller
                                                </Typography>
                                            </div>
                                        </Radio>
                                    </div>
                                </Radio.Group>
                            </div>
                        </div>
                    )}

                    {isRegularMode && (
                        <div className="extensions-list-section">
                            <div className="live-calls-container">
                                <div className="live-calls-body-container">
                                    <div className='user-list-active-calls-container'>
                                        <div className='users-list-container'>
                                            <ul className="list-container" role="list">
                                                {ext && (
                                                    <TransferModalExtensionsList
                                                        onTransfer={handleExtensionSelect}
                                                        excludeExts={excludeExts}
                                                        itemHeight={80}
                                                        searchTerm={ext}
                                                    />
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {isDndMode && (
                    <div className="modal-footer">
                        <Button
                            type="primary"
                            className="confirm-button"
                            onClick={handleConfirm}
                            disabled={!selectedLeg}
                        >
                            Confirm Transfer
                        </Button>
                        <Button className="cancel-button" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default React.memo(TransferCallModal);
